import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Card, Typography } from "@mui/material";
import {
  CodeOutlined,
  StorageOutlined,
  TrendingUpOutlined,
  SecurityOutlined,
  SpeedOutlined,
  LightbulbOutlined,
  PsychologyOutlined,
  PeopleAltOutlined,
} from "@mui/icons-material";

interface BoxType {
  id: string;
  ruta: string;
  icon: React.ReactNode;
}

export default function CajasDeslizantes() {
  const { t } = useTranslation();

  const boxes: BoxType[] = [
    {
      id: "desarrollo",
      ruta: "/servicios#desarrollo",
      icon: <CodeOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "migracion",
      ruta: "/servicios#migracion",
      icon: <StorageOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "digitalizacion",
      ruta: "/servicios#digitalizacion",
      icon: <TrendingUpOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "data",
      ruta: "/servicios#data",
      icon: <SecurityOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "incident",
      ruta: "/servicios#incident",
      icon: <SpeedOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "consultoria",
      ruta: "/servicios#consultoria",
      icon: <LightbulbOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "ia",
      ruta: "/servicios#ia",
      icon: <PsychologyOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
    {
      id: "staff",
      ruta: "/equipos-especializados",
      icon: <PeopleAltOutlined sx={{ fontSize: "2rem", color: "#0069a7" }} />,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          sm: "repeat(2, minmax(0, 1fr))",
          lg: "repeat(4, minmax(0, 1fr))",
        },
        gap: { xs: 2, sm: 3, md: 5, lg: 5.5 },
        mt: 0,
        mb: 0,
        alignItems: "stretch",
      }}
    >
      {boxes.map((box, index) => {
        const iconColor = index % 2 === 0 ? "#0050d1" : "#06b6d4";
        const clonedIcon = React.cloneElement(box.icon as React.ReactElement<any>, {
          sx: { fontSize: "2rem", color: iconColor }
        });

        return (
          <Box key={box.id} sx={{ display: "flex", height: "100%", width: "100%" }}>
            <Link
              to={box.ruta}
              style={{ textDecoration: "none", display: "flex", width: "100%", height: "100%" }}
            >
              <Card
                className="group"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  p: { xs: 2.2, sm: 3.5 },
                  py: { xs: 3, sm: 4 },
                  width: "100%",
                  height: "100%",
                  minHeight: { xs: "170px", sm: "220px" },
                  boxSizing: "border-box",
                  bgcolor: "#ffffff",
                  borderRadius: "1.75rem",
                  boxShadow: "0 4px 30px rgba(0,0,0,0.02)",
                  border: "1px solid #f1f5f9",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-10px)",
                    boxShadow: "0 25px 60px rgba(10,17,40,0.08)",
                  },
                }}
              >
                {/* ICON CONTAINER */}
                <Box
                  sx={{
                    width: { xs: "48px", sm: "56px" },
                    height: { xs: "48px", sm: "56px" },
                    borderRadius: { xs: "14px", sm: "16px" },
                    bgcolor: "rgba(6, 182, 212, 0.08)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: { xs: 2, sm: 3 },
                    flexShrink: 0,
                    transition: "transform 0.3s ease",
                    ".group:hover &": {
                      transform: "scale(1.1)",
                    }
                  }}
                >
                  {clonedIcon}
                </Box>

                {/* TITLE */}
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "0.92rem", sm: "1.05rem", md: "1.125rem" },
                    color: "#002766",
                    textAlign: "left",
                    lineHeight: 1.375,
                    transition: "color 0.3s ease",
                    ".group:hover &": {
                      color: "#0050d1",
                    }
                  }}
                >
                  {t(`nav.servicios_list.${box.id}`)}
                </Typography>
              </Card>
            </Link>
          </Box>
        );
      })}
    </Box>
  );
}